import {
    getPartnerAppointmentDetail,
    getPartnerToken,
} from '@/lib/api/partner-portal/partner-portal-api'
import { formatDateOnly } from '@/lib/utils/date-only'
import { PartnerAppointmentDetail } from '@/lib/api/partner-portal/partner-portal.types'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useEffect, useState } from 'react'
import {
    MdArrowBack,
    MdCalendarToday,
    MdDescription,
    MdLocalHospital,
    MdPerson,
    MdVideocam,
} from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

const TYPE_LABELS: Record<string, string> = {
    CONSULTATION: 'Consulta',
    TELECONSULTATION: 'Teleconsulta',
    EXAM: 'Exame',
    PROCEDURE: 'Procedimento',
}

const STATUS_LABELS: Record<string, string> = {
    SCHEDULED: 'Agendado',
    WAITING: 'Aguardando',
    IN_PROGRESS: 'Em andamento',
    COMPLETED: 'Concluído',
    CANCELLED: 'Cancelado',
    NO_SHOW: 'Faltou',
    REQUESTED: 'Solicitado',
    POSTPONED: 'Adiado',
    CREATED: 'Criada',
    WAITING_PATIENT: 'Aguardando paciente',
    WAITING_DOCTOR: 'Aguardando profissional',
    EXPIRED: 'Expirada',
}

const STATUS_BADGE_CLASSES: Record<string, string> = {
    SCHEDULED: 'badge-warning',
    WAITING: 'badge-info',
    IN_PROGRESS: 'badge-secondary',
    COMPLETED: 'badge-success',
    CANCELLED: 'badge-error',
    NO_SHOW: 'badge-error',
    REQUESTED: 'badge-warning',
    POSTPONED: 'badge-ghost',
}

const GENDER_LABELS: Record<string, string> = {
    MALE: 'Masculino',
    FEMALE: 'Feminino',
    OTHER: 'Outro',
}

function formatDate(dateStr?: string | null) {
    if (!dateStr) return '-'
    try {
        return format(new Date(dateStr), "dd/MM/yyyy 'as' HH:mm", { locale: ptBR })
    } catch {
        return '-'
    }
}

function formatDuration(minutes?: number | null) {
    if (!minutes) return '-'
    if (minutes < 60) return `${minutes} min`
    const h = Math.floor(minutes / 60)
    const m = minutes % 60
    return m > 0 ? `${h}h ${m}min` : `${h}h`
}

function InfoRow({ label, value }: { label: string; value?: string | null | React.ReactNode }) {
    if (!value || value === '-') return null
    return (
        <div className="flex flex-col sm:flex-row sm:items-start gap-1 py-2 border-b border-base-200 last:border-0">
            <span className="text-sm font-medium text-base-content/60 sm:w-48 shrink-0">
                {label}
            </span>
            <span className="text-sm text-base-content">{value}</span>
        </div>
    )
}

export default function PartnerAppointmentDetailPage() {
    const { type, id } = useParams<{ type: string; id: string }>()
    const navigate = useNavigate()
    const [detail, setDetail] = useState<PartnerAppointmentDetail | null>(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        if (!getPartnerToken()) {
            navigate('/partner', { replace: true })
            return
        }

        if (!type || !id) {
            navigate('/partner/appointments', { replace: true })
            return
        }

        fetchDetail()
    }, [type, id])

    const fetchDetail = async () => {
        setLoading(true)
        const response = await getPartnerAppointmentDetail(type!, parseInt(id!, 10))

        if (response.success && response.result) {
            setDetail(response.result)
        } else {
            toast.error(response.error || 'Erro ao buscar detalhes.')
            navigate('/partner/appointments', { replace: true })
        }
        setLoading(false)
    }

    if (loading) {
        return (
            <div className="flex justify-center py-24" data-theme="atendimento">
                <span className="loading loading-spinner loading-lg" />
            </div>
        )
    }

    if (!detail) return null

    const client = detail.client
    const professional = detail.professional

    const clientAddress = [
        client.street,
        client.number,
        client.complement,
        client.neighborhood,
        client.city,
        client.state,
        client.zipCode,
    ]
        .filter(Boolean)
        .join(', ')

    return (
        <div className="space-y-6 !max-w-[1200px] !mx-auto">
            {/* Header */}
            <div className="flex items-center gap-3">
                <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => navigate('/partner/appointments')}
                >
                    <MdArrowBack className="text-lg" />
                </button>
                <div className="flex-1">
                    <h1 className="text-xl font-bold text-base-content">
                        {detail.title || TYPE_LABELS[detail.type] || 'Agendamento'}
                    </h1>
                    <div className="flex items-center gap-2 mt-1">
                        <span
                            className={`badge badge-sm ${STATUS_BADGE_CLASSES[detail.status] || 'badge-ghost'
                                }`}
                        >
                            {STATUS_LABELS[detail.status] || detail.status}
                        </span>
                        <span className="text-sm text-base-content/60">
                            {TYPE_LABELS[detail.type]}
                        </span>
                        {detail.isReturn && (
                            <span className="badge badge-sm badge-outline">Retorno</span>
                        )}
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Dados do Paciente */}
                <div className="card bg-base-100 border border-secondary/20">
                    <div className="card-body">
                        <h2 className="card-title text-base gap-2">
                            <MdPerson className="text-lg text-secondary" />
                            Paciente
                        </h2>
                        <div className="mt-2">
                            <InfoRow label="Nome" value={client.name} />
                            {client.socialName && (
                                <InfoRow label="Nome social" value={client.socialName} />
                            )}
                            <InfoRow label="CPF" value={client.cpf} />
                            <InfoRow label="E-mail" value={client.email} />
                            <InfoRow label="WhatsApp" value={client.whatsAppNumber} />
                            <InfoRow
                                label="Data de nascimento"
                                value={formatDateOnly(client.birthDate)}
                            />
                            <InfoRow
                                label="Sexo"
                                value={client.gender ? GENDER_LABELS[client.gender] || client.gender : null}
                            />
                            {clientAddress && (
                                <InfoRow label="Endereço" value={clientAddress} />
                            )}
                        </div>
                    </div>
                </div>

                {/* Dados do Agendamento */}
                <div className="card bg-base-100 border border-secondary/20">
                    <div className="card-body">
                        <h2 className="card-title text-base gap-2">
                            <MdCalendarToday className="text-lg text-secondary" />
                            Agendamento
                        </h2>
                        <div className="mt-2">
                            <InfoRow label="Data/Hora" value={formatDate(detail.scheduledAt)} />
                            <InfoRow label="Início" value={formatDate(detail.startedAt)} />
                            <InfoRow label="Conclusão" value={formatDate(detail.completedAt)} />
                            <InfoRow label="Duração" value={formatDuration(detail.duration)} />
                            {detail.requestedAt && (
                                <InfoRow
                                    label="Data da solicitação"
                                    value={formatDate(detail.requestedAt)}
                                />
                            )}
                            <InfoRow label="Criado em" value={formatDate(detail.createdAt)} />
                            {detail.specialty && (
                                <InfoRow label="Especialidade" value={detail.specialty.name} />
                            )}
                            {detail.catalog && (
                                <InfoRow
                                    label="Tipo de consulta"
                                    value={detail.catalog.name}
                                />
                            )}
                            {detail.healthPlan && (
                                <InfoRow label="Convenio" value={detail.healthPlan.name} />
                            )}
                            {detail.tussCode && (
                                <InfoRow label="Código TUSS" value={detail.tussCode} />
                            )}
                            {detail.room && <InfoRow label="Sala" value={detail.room} />}
                            {detail.equipment && (
                                <InfoRow label="Equipamento" value={detail.equipment} />
                            )}
                        </div>
                    </div>
                </div>

                {/* Profissional */}
                {professional && (
                    <div className="card bg-base-100 border border-secondary/20">
                        <div className="card-body">
                            <h2 className="card-title text-base gap-2">
                                <MdLocalHospital className="text-lg text-secondary" />
                                Profissional
                            </h2>
                            <div className="mt-2">
                                <InfoRow label="Nome" value={professional.name} />
                                {professional.councilName && professional.registrationNumber && (
                                    <InfoRow
                                        label="Registro"
                                        value={`${professional.councilName} ${professional.registrationNumber}`}
                                    />
                                )}
                                {professional.specialties && professional.specialties.length > 0 && (
                                    <InfoRow
                                        label="Especialidades"
                                        value={professional.specialties.join(', ')}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/*
                    O portal do parceiro NÃO exibe valores — nenhum. Ele mostra a
                    agenda: quem, quando, onde. Valor e desconto revelam a política
                    comercial da empresa (e, junto com o que o parceiro recebe, o
                    markup praticado). O card "Valor" que existia aqui foi removido
                    junto com o campo `price` na API. Mesma regra dos PDFs do
                    parceiro, que já saem sem preços.
                */}

                {/* Teleconsulta */}
                {detail.teleconsultationRooms && detail.teleconsultationRooms.length > 0 && (
                    <div className="card bg-base-100 border border-secondary/20">
                        <div className="card-body">
                            <h2 className="card-title text-base gap-2">
                                <MdVideocam className="text-lg text-secondary" />
                                Teleconsulta
                            </h2>
                            {detail.teleconsultationRooms.map(room => (
                                <div key={room.id} className="mt-2">
                                    <InfoRow
                                        label="Status"
                                        value={
                                            <span
                                                className={`badge badge-sm ${STATUS_BADGE_CLASSES[room.status] || 'badge-ghost'
                                                    }`}
                                            >
                                                {STATUS_LABELS[room.status] || room.status}
                                            </span>
                                        }
                                    />
                                    <InfoRow
                                        label="Agendada para"
                                        value={formatDate(room.scheduledAt)}
                                    />
                                    <InfoRow label="Início" value={formatDate(room.startedAt)} />
                                    <InfoRow label="Fim" value={formatDate(room.endedAt)} />
                                    <InfoRow
                                        label="Duração"
                                        value={formatDuration(room.duration)}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Observações e Detalhes Clinicos */}
                {(detail.notes || detail.diagnosis || detail.treatmentPlan || detail.description || detail.preInstructions || detail.postInstructions || detail.outcome) && (
                    <div className="card bg-base-100 border border-secondary/20 lg:col-span-2">
                        <div className="card-body">
                            <h2 className="card-title text-base gap-2">
                                <MdDescription className="text-lg text-secondary" />
                                Informações Adicionais
                            </h2>
                            <div className="mt-2 space-y-4">
                                {detail.description && (
                                    <div>
                                        <p className="text-sm font-medium text-base-content/60 mb-1">
                                            Descrição
                                        </p>
                                        <p className="text-sm text-base-content whitespace-pre-wrap">
                                            {detail.description}
                                        </p>
                                    </div>
                                )}
                                {detail.notes && (
                                    <div>
                                        <p className="text-sm font-medium text-base-content/60 mb-1">
                                            Observações
                                        </p>
                                        <p className="text-sm text-base-content whitespace-pre-wrap">
                                            {detail.notes}
                                        </p>
                                    </div>
                                )}
                                {detail.diagnosis && (
                                    <div>
                                        <p className="text-sm font-medium text-base-content/60 mb-1">
                                            Diagnóstico
                                        </p>
                                        <p className="text-sm text-base-content whitespace-pre-wrap">
                                            {detail.diagnosis}
                                        </p>
                                    </div>
                                )}
                                {detail.treatmentPlan && (
                                    <div>
                                        <p className="text-sm font-medium text-base-content/60 mb-1">
                                            Plano de tratamento
                                        </p>
                                        <p className="text-sm text-base-content whitespace-pre-wrap">
                                            {detail.treatmentPlan}
                                        </p>
                                    </div>
                                )}
                                {detail.preInstructions && (
                                    <div>
                                        <p className="text-sm font-medium text-base-content/60 mb-1">
                                            Instruções pré-procedimento
                                        </p>
                                        <p className="text-sm text-base-content whitespace-pre-wrap">
                                            {detail.preInstructions}
                                        </p>
                                    </div>
                                )}
                                {detail.postInstructions && (
                                    <div>
                                        <p className="text-sm font-medium text-base-content/60 mb-1">
                                            Instruções pós-procedimento
                                        </p>
                                        <p className="text-sm text-base-content whitespace-pre-wrap">
                                            {detail.postInstructions}
                                        </p>
                                    </div>
                                )}
                                {detail.outcome && (
                                    <div>
                                        <p className="text-sm font-medium text-base-content/60 mb-1">
                                            Resultado
                                        </p>
                                        <p className="text-sm text-base-content whitespace-pre-wrap">
                                            {detail.outcome}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
